/* Карточки персонажей каталога и картинки их видов для экранов движка («Говорящий кружок», «Рилс с вами» и «Вы в кино»).
   Правила:
   - карточка (avatarCatalog.details) — одна на приложение, персонажа и язык (язык — в ключе): второй экран берёт её из памяти, запрос не повторяется;
     не пришла — null (виды и голоса персонажа неизвестны, экран живёт без них);
   - картинка вида — ключ хранилища live-avatar/… отдаёт авторизованный BFF GET /app/catalog/avatars/{id}/image?skin_id= (use-circle-skin-art.ts):
     она идёт в общий кэш картинок студии (studio-artwork-cache), одна аренда на персонажа, вид и версию; другие значения (адрес https) — как есть. */
import { api, endpoints } from '../../../api';
import type { AvatarDetails } from '../../../api/avatar-catalog';
import { accountLocale } from '../../../i18n';
import { isSkinKey } from '../circle/use-circle-skin-art';
import { leaseArtwork, type ArtworkLease } from '../studio-artwork-cache';

/** Язык карточек каталога */
export const cardLocale = () => (accountLocale() === 'ru' ? 'ru' : 'en');
/** Карточки по ключу detailsKey: null — не пришла */
export const details = new Map<string, AvatarDetails | null>();
const detailsFlight = new Map<string, Promise<AvatarDetails | null>>();
export const detailsKey = (id: number) => `${id}:${cardLocale()}`;
export function loadDetails(id: number): Promise<AvatarDetails | null> {
  const key = detailsKey(id);
  if (details.has(key)) return Promise.resolve(details.get(key) ?? null);
  let flight = detailsFlight.get(key);
  if (!flight) {
    flight = endpoints.avatarCatalog
      .details(id, cardLocale())
      .then(
        value => (value?.avatar?.id === id ? value : null),
        () => null,
      )
      .then(value => {
        details.set(key, value);
        return value;
      })
      .finally(() => detailsFlight.delete(key));
    detailsFlight.set(key, flight);
  }
  return flight;
}

/** Картинки видов: ключ — персонаж, вид и версия карточки; url — blob-адрес, пока не пришёл — null */
export const skinArt = new Map<string, { lease: ArtworkLease; url: string | null }>();
const artKey = (card: AvatarDetails, skinId: number) => `${card.avatar.id}:s${skinId}:${card.avatar.version}`;
/** Взять картинку вида в аренду (одна на ключ); ready — когда адрес пришёл (только у новой аренды) */
export function leaseSkinArt(card: AvatarDetails, skinId: number, ready: (url: string) => void) {
  const key = artKey(card, skinId);
  if (skinArt.has(key)) return;
  const epoch = endpoints.sessionEpoch?.() ?? 0;
  const lease = leaseArtwork(epoch, key, () => api.download(`/app/catalog/avatars/${card.avatar.id}/image?skin_id=${skinId}`));
  const entry = { lease, url: null as string | null };
  skinArt.set(key, entry);
  lease.url.then(
    url => {
      entry.url = url;
      ready(url);
    },
    () => {},
  );
}
/** Взять в аренду картинки всех видов карточки, которые лежат в хранилище */
export function leaseSkins(card: AvatarDetails, ready: (skinId: number) => void) {
  for (const sk of card.avatar.skins) if (isSkinKey(sk.image_url)) leaseSkinArt(card, sk.id, () => ready(sk.id));
}
/** Адрес картинки вида skinId карточки: адрес https — как есть, ключ хранилища — blob аренды; нет (не пришла, вида нет) — null */
export function skinUrl(card: AvatarDetails, skinId: string | number): string | null {
  const sk = card.avatar.skins.find(item => String(item.id) === String(skinId));
  if (!sk) return null;
  if (!isSkinKey(sk.image_url)) return sk.image_url || null;
  return skinArt.get(artKey(card, sk.id))?.url ?? null;
}
